import React, { useCallback, useMemo } from 'react';
import moment from 'moment';

import Avatar, { AvatarType } from '@rover/components/src/Avatar';
import ClampedParagraph from '@rover/components/src/typography/ClampedParagraph';
import { Flex, Text } from '@rover/kibble/core';
import StarRating from '@rover/kibble/patterns/StarRating';
import useTheme from '@rover/kibble/themes/useTheme';
import { withOpacity } from '@rover/kibble/utils';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';
import type { PetData } from '@rover/rsdk/src/apiClient/latest/model/petData';
import type { ProviderRating } from '@rover/rsdk/src/apiClient/latest/model/providerRating';

// The webpack build bundles only moment's English locales, so `.locale(locale)` needs the rest loaded explicitly.
import '@rover/utilities/allMomentLocales';

import Card, { CardLink } from '../Card';

type ReviewPet = Pick<PetData, 'petType'> & {
  defaultImage: Pick<PetData['defaultImage'], 'bigThumb'>;
};

type Review = Pick<ProviderRating, 'description' | 'overall' | 'added'> & {
  poster: Pick<ProviderRating['poster'], 'shortName'>;
  service: Pick<ProviderRating['service'], 'name'>;
  pets: ReviewPet[];
};

// require `rootCarouselRef` if `onViewed` is provided
type OnViewedProps =
  | { rootCarouselRef?: React.RefObject<HTMLElement | undefined>; onViewed?: never }
  | {
      rootCarouselRef: React.RefObject<HTMLElement | undefined>;
      onViewed: (review: Review) => void;
    };

type Props = {
  review: Review;
  href?: string;
  hrefLabel?: string;
} & OnViewedProps;

function getAvatarTypeForPetType(petType: PetData['petType']): AvatarType {
  switch (petType) {
    case 'dog':
    case 'cat':
      return petType as AvatarType;

    default:
      return 'other' as AvatarType;
  }
}

const CarouselReviewCard: React.FC<Props> = ({
  review,
  onViewed,
  rootCarouselRef,
  href,
  hrefLabel,
  ...props
}) => {
  const { colors } = useTheme();
  const {
    description,
    overall,
    added,
    poster,
    service,
    pets: [pet],
  } = review;

  const { DF, locale } = useI18nContext();
  // Parse the timezone to prevent React hydration errors.
  const formattedAdded = useMemo(
    () => moment.parseZone(added).locale(locale).format(DF.MONTH_DAY_MED),
    [added, locale, DF.MONTH_DAY_MED]
  );

  const handleCardVisible = useCallback(() => {
    onViewed?.(review);
  }, [onViewed, review]);

  const cardRef = useOnIntersect(handleCardVisible, true, {
    root: rootCarouselRef?.current,
    threshold: 0.75,
  });

  return (
    <Flex
      htmlRef={cardRef}
      maxWidth={[300, 350]}
      minWidth={[300, 350]}
      width={[300, 350]}
      sx={{ boxShadow: `0px 1px 4px 0px ${withOpacity(colors.neutral['900'], 0.23)}` }}
      borderRadius="8px"
      position="relative"
      flexDirection="column"
      height="100%"
      {...props}
    >
      {href && hrefLabel && <CardLink href={href} aria-label={hrefLabel} />}
      <Card
        borderRadius="8px"
        background="primary"
        cardStyle="inset"
        isStacked
        padding="5x"
        height="100%"
      >
        <Flex flexDirection="column" gap="4x" justifyContent="space-between" height="100%">
          <Flex flexDirection="column" gap="4x">
            <StarRating rating={overall} variant="expand" />
            <ClampedParagraph lines={6}>{description}</ClampedParagraph>
          </Flex>
          <Flex alignItems="center">
            <Avatar
              diameter={32}
              borderRadius="50%"
              imageUrl={pet?.defaultImage?.bigThumb}
              avatarType={getAvatarTypeForPetType(pet?.petType)}
            />
            <Flex flexDirection="column" marginLeft="3x" flexGrow="1">
              <Text size="200">{poster.shortName}</Text>
              <Flex justifyContent="space-between" gap="1x">
                <Text size="100" sx={{ color: 'neutral.700' }}>
                  <time dateTime={added}>{formattedAdded}</time>
                </Text>
                <Text size="100" sx={{ color: 'neutral.700' }}>
                  {service.name}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  );
};

export default CarouselReviewCard;
